import type { IMeshBounds } from "@project/emerge-types";
import { ReferenceGuideType } from "@project/emerge-types";
import { OrbitControls, PivotControls } from "@react-three/drei";
import { Canvas, type ThreeEvent, useThree } from "@react-three/fiber";
import type React from "react";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  Box3,
  Color,
  type Group,
  type Material,
  Matrix3,
  Mesh,
  MeshBasicMaterial,
  Object3D,
  type PerspectiveCamera,
  Plane,
  Raycaster,
  SphereGeometry,
  Vector2,
  Vector3,
} from "three";
import type { OrbitControls as OrbitControlsImpl } from "three-stdlib";
import {
  getWorldAxisDirection as getWorldAxisDirectionFromModel,
  ThreeToolsInstance,
} from "../../index"; // Corrected import path
import { ProductViewerStage } from "../new/ProductViewerStage";
import {
  convertToModernMaterial,
  setMaterialsDoubleSided,
} from "../new/SupplyPartRenderer";

// Define orientation types
type OrientationName = "FRONT" | "BACK" | "UP" | "DOWN" | "RIGHT" | "LEFT";
type OrientationData = {
  name: OrientationName;
  rotation: [number, number, number];
};

// Define the six possible cube orientations
const ORIENTATIONS: OrientationData[] = [
  { name: "FRONT", rotation: [0, 0, 0] },
  { name: "BACK", rotation: [0, ThreeToolsInstance.DEG2RAD(180), 0] },
  { name: "UP", rotation: [ThreeToolsInstance.DEG2RAD(90), 0, 0] },
  { name: "DOWN", rotation: [ThreeToolsInstance.DEG2RAD(-90), 0, 0] },
  { name: "RIGHT", rotation: [0, ThreeToolsInstance.DEG2RAD(-90), 0] },
  { name: "LEFT", rotation: [0, ThreeToolsInstance.DEG2RAD(90), 0] },
];

// Define the four possible rotations around the current orientation axis (in degrees)
const ROTATIONS = [0, 90, 180, 270];

// Type for rotation array (in radians)
type RotationArray = [number, number, number];

const measureMeshBounds = (object: Object3D): IMeshBounds | null => {
  object.updateWorldMatrix(true, true);

  const box = new Box3().setFromObject(object);
  if (box.isEmpty()) {
    return null;
  }

  const size = box.getSize(new Vector3());
  const center = box.getCenter(new Vector3());

  if (
    !Number.isFinite(size.x) ||
    !Number.isFinite(size.y) ||
    !Number.isFinite(size.z) ||
    !Number.isFinite(center.x) ||
    !Number.isFinite(center.y) ||
    !Number.isFinite(center.z)
  ) {
    return null;
  }

  if (size.x <= 0 || size.y <= 0 || size.z <= 0) {
    return null;
  }

  return {
    width: size.x,
    height: size.y,
    depth: size.z,
    offsetX: center.x,
    offsetY: center.y,
    offsetZ: center.z,
  };
};

// Component to handle manual camera fitting
const CameraFitter = ({
  groupRef,
  controlsRef,
}: {
  groupRef: React.RefObject<Group | null>;
  controlsRef: React.RefObject<OrbitControlsImpl | null>;
}) => {
  const { camera } = useThree();

  useEffect(() => {
    if (groupRef.current && "fov" in camera) {
      // Calculate bounding box of the group
      const box = new Box3().setFromObject(groupRef.current);
      const size = box.getSize(new Vector3());
      const center = box.getCenter(new Vector3());

      // Calculate distance to fit the object
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = (camera as PerspectiveCamera).fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));

      // Add some margin
      cameraZ *= 1.5;

      // Set camera position immediately without animation
      camera.position.set(center.x, center.y, center.z + cameraZ);
      camera.lookAt(center);

      // Align OrbitControls target to the fitted center to avoid perceived damping/inertia
      if (controlsRef.current) {
        controlsRef.current.target.copy(center);
        controlsRef.current.update();
      }
    }
  }, [camera, groupRef, controlsRef]);

  return null;
};

// Internal Model component
const Model = ({
  url,
  transform,
  materialOverride,
  /** When true and no materialOverride: keep GLTF materials (like SupplyPartRenderer without a forced material). */
  preferLoadedMeshMaterials = false,
  fallbackMaterial,
  baseRotationX = 0,
  onModelLoaded,
  onBoundsMeasured,
  referenceGuideType,
  onCtrlClick,
}: {
  url: string;
  transform: {
    position: [number, number, number];
    rotation: [number, number, number];
    scale: number;
  };
  materialOverride?: Material;
  preferLoadedMeshMaterials?: boolean;
  fallbackMaterial?: Material;
  baseRotationX?: number;
  onModelLoaded?: (model: Object3D | null) => void;
  onBoundsMeasured?: (bounds: IMeshBounds | null) => void;
  referenceGuideType: ReferenceGuideType;
  onCtrlClick?: (worldPoint: Vector3, worldNormal: Vector3 | null) => void;
}) => {
  const [model, setModel] = useState<Object3D | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadModel = async () => {
      try {
        // Load the mesh - type will be inferred (likely Group | Mesh | LineSegments etc.)
        const loadedObject = await ThreeToolsInstance.loadMesh({ model: url });

        // Debug-Ausgabe nach dem Laden
        if (!loadedObject) {
          console.warn(
            "[MeshEditor/Model] Mesh konnte nicht geladen werden:",
            url,
          );
        }

        if (!isMounted) return;

        onBoundsMeasured?.(
          loadedObject ? measureMeshBounds(loadedObject) : null,
        );

        // Traverse the loaded object to apply material and compute bounding sphere
        loadedObject?.traverse((child) => {
          if (child instanceof Mesh) {
            if (materialOverride) {
              const m = materialOverride.clone();
              setMaterialsDoubleSided(m);
              child.material = m;
            } else if (!preferLoadedMeshMaterials && fallbackMaterial) {
              // Caller-provided fallback (e.g. corners get the sheet metal material
              // from `manufacturer.materials` — single source of truth).
              const m = fallbackMaterial.clone();
              setMaterialsDoubleSided(m);
              child.material = m;
            } else if (child.material) {
              child.material = convertToModernMaterial(child.material);
              setMaterialsDoubleSided(child.material);
            }
            if (child.geometry) {
              child.geometry.computeBoundingSphere();
            }
          }
        });

        // Apply scale - scale exists on Object3D base type
        if (transform.scale != null) {
          const scale = transform.scale;
          loadedObject?.scale.set(scale, scale, scale);
        }

        // Create the hierarchy of transformations
        const sunlabsNormalize = new Object3D();
        const position = transform.position;
        sunlabsNormalize.position.set(-position[0], -position[2], position[1]);

        if (transform.rotation != null) {
          const rotation = transform.rotation;
          // Convert degrees to radians for internal use
          sunlabsNormalize.rotation.set(
            ThreeToolsInstance.DEG2RAD(rotation[0]),
            ThreeToolsInstance.DEG2RAD(rotation[1]),
            ThreeToolsInstance.DEG2RAD(rotation[2]),
          );
        }

        if (loadedObject) {
          sunlabsNormalize.add(loadedObject); // Add the potentially complex loaded object
        }

        // Base rotation group applied BEFORE editor rotation (parent transform)
        // Use baseRotationX for editor-specific rotation (e.g., for axis controls)
        const baseRotationGroup = new Object3D();
        baseRotationGroup.rotation.set(baseRotationX, 0, 0);
        baseRotationGroup.add(sunlabsNormalize);

        // Origin marker (red sphere) showing the object's zero point
        // Rotate around GLOBAL origin by translating a child, then rotating the parent
        const originMarker = new Mesh(
          new SphereGeometry(2, 16, 16),
          // Distinct color to differentiate from any other origin spheres
          new MeshBasicMaterial({ color: new Color(0x3399ff) }),
        );
        const originMarkerNode = new Object3D();
        const worldPosForMarker = transformPositionToWorld(position);
        originMarkerNode.position.copy(worldPosForMarker);
        originMarkerNode.add(originMarker);
        baseRotationGroup.add(originMarkerNode);

        // Apply the same final rotation as SupplyPartRenderer (180° around X)
        // BUT: Skip this rotation for parts with HorizontalPlane or VerticalPlane reference guides
        // This ensures the mesh editor shows the same orientation as in the product viewer
        const localTransformation = new Object3D();
        localTransformation.add(baseRotationGroup);
        const shouldApplyFinalRotation =
          referenceGuideType !== ReferenceGuideType.HorizontalPlane &&
          referenceGuideType !== ReferenceGuideType.VerticalPlane;
        if (shouldApplyFinalRotation) {
          localTransformation.rotation.set(
            ThreeToolsInstance.DEG2RAD(180),
            0,
            0,
          );
        } else {
          localTransformation.rotation.set(0, 0, 0);
        }

        setModel(localTransformation);
        onModelLoaded?.(localTransformation);
      } catch (error) {
        console.error("Error loading model:", error);
        onBoundsMeasured?.(null);
        onModelLoaded?.(null);
      }
    };

    void loadModel();

    return () => {
      isMounted = false;
    };
  }, [
    url,
    transform,
    materialOverride,
    preferLoadedMeshMaterials,
    fallbackMaterial,
    baseRotationX,
    referenceGuideType,
    onBoundsMeasured,
    onModelLoaded,
  ]);

  const handleCtrlClick = useCallback(
    (e: ThreeEvent<MouseEvent>) => {
      if (!e.nativeEvent.ctrlKey) return;
      e.stopPropagation();

      const worldPoint = e.point;
      let worldNormal: Vector3 | null = null;

      if (e.face) {
        const normalMatrix = new Matrix3().getNormalMatrix(
          e.object.matrixWorld,
        );
        worldNormal = e.face.normal
          .clone()
          .applyMatrix3(normalMatrix)
          .normalize();
      }

      onCtrlClick?.(worldPoint, worldNormal);
    },
    [onCtrlClick],
  );

  return model ? (
    // biome-ignore lint/a11y/noStaticElementInteractions: R3F group receives bubbled events from child meshes
    <group onClick={handleCtrlClick}>
      <primitive object={model} />
    </group>
  ) : null;
};

type Axis = "x" | "y" | "z";
type AxisType = false | Axis;
type Position = [number, number, number];
type Transform = {
  position: Position;
  rotation: Position;
  scale: number;
};
type BeginAxisDragFn = (axis: Axis, startPoint: Vector3) => void;

/**
 * Converts transform.position (Sunlabs coordinate system) to World Space coordinates.
 * This is the legacy transformation from the Sunlabs coordinate system.
 *
 * @param position - Position in transform.position format [x, y, z]
 * @returns World Space position as Vector3
 */
function transformPositionToWorld(position: Position): Vector3 {
  return new Vector3(-position[0], -position[2], position[1]);
}

// Props for the MeshEditorView component
export interface MeshEditorViewProps {
  url: string;
  transform: Transform;
  contextScene?: ReactNode; // Additional 3D content to render alongside the mesh
  materialOverride?: Material;
  /**
   * When true and no materialOverride: use GLTF materials (convertToModernMaterial)
   * instead of the shiny metal placeholder — matches SupplyPartRenderer without a forced material.
   */
  preferLoadedMeshMaterials?: boolean;
  /**
   * Material applied when there is no `materialOverride` and
   * `preferLoadedMeshMaterials` is false (e.g. the sheet metal material for
   * corners). Provided by the caller so material definitions stay in a single
   * source of truth (`manufacturer.materials`).
   */
  fallbackMaterial?: Material;
  enableInteractiveControls?: boolean; // Whether to enable interactive pivot controls
  baseRotationX?: number; // base X rotation (radians) applied before editor rotation
  onPositionChange?: (position: Position) => void;
  onModelLoaded?: (model: Object3D | null) => void; // Callback when model is loaded
  onBoundsMeasured?: (bounds: IMeshBounds | null) => void; // Callback when mesh bounds are measured
  modelRef?: React.RefObject<Object3D | null>; // Ref to the loaded model for axis direction calculation
  referenceGuideType: ReferenceGuideType; // Reference guide type to determine if 180° X rotation should be applied
}

// Component inside Canvas to provide axis drag handlers with access to Three.js context
const AxisDragProvider = ({
  axisDragRef,
  dragStartPointRef,
  dragStartDomainRef,
  onPositionChange,
  transform,
  onBeginAxisDrag,
  onEndAxisDrag,
  beginAxisDragRef,
  modelRef,
  baseRotationX = 0,
  referenceGuideType,
}: {
  axisDragRef: React.RefObject<AxisType>;
  dragStartPointRef: React.RefObject<Vector3 | null>;
  dragStartDomainRef: React.RefObject<Position | null>;
  onPositionChange?: (position: Position) => void;
  transform: Transform;
  onBeginAxisDrag: (axis: Axis) => void;
  onEndAxisDrag: () => void;
  beginAxisDragRef: React.RefObject<BeginAxisDragFn | null>;
  modelRef?: React.RefObject<Object3D | null>;
  baseRotationX?: number;
  referenceGuideType: ReferenceGuideType;
}) => {
  const { camera, gl } = useThree();
  const raycasterRef = useRef(new Raycaster());
  const listenerCleanupRef = useRef<(() => void) | null>(null);
  /**
   * Raycast from screen position onto a plane.
   */
  const raycastOntoPlane = useCallback(
    (clientX: number, clientY: number, plane: Plane): Vector3 | null => {
      const rect = gl.domElement.getBoundingClientRect();
      const ndc = new Vector2(
        ((clientX - rect.left) / rect.width) * 2 - 1,
        -((clientY - rect.top) / rect.height) * 2 + 1,
      );
      raycasterRef.current.setFromCamera(ndc, camera);
      const ray = raycasterRef.current.ray;
      const intersection = new Vector3();
      const result = ray.intersectPlane(plane, intersection);
      return result;
    },
    [camera, gl.domElement],
  );

  /**
   * Converts a world space delta to transform.position delta, accounting for
   * the conditional 180° X rotation and baseRotationX.
   */
  const shouldApplyFinalRotation =
    referenceGuideType !== ReferenceGuideType.HorizontalPlane &&
    referenceGuideType !== ReferenceGuideType.VerticalPlane;

  const worldDeltaToTransformDelta = useCallback(
    (worldDelta: Vector3): Position => {
      // Undo the 180° X rotation applied during rendering (flips Y and Z)
      const correctedY = shouldApplyFinalRotation
        ? -worldDelta.y
        : worldDelta.y;
      const correctedZ = shouldApplyFinalRotation
        ? -worldDelta.z
        : worldDelta.z;

      const cosBaseRot = Math.cos(-baseRotationX);
      const sinBaseRot = Math.sin(-baseRotationX);

      const localY = correctedY * cosBaseRot - correctedZ * sinBaseRot;
      const localZ = correctedY * sinBaseRot + correctedZ * cosBaseRot;

      return [-worldDelta.x, localZ, -localY];
    },
    [baseRotationX, shouldApplyFinalRotation],
  );

  /**
   * Gets the world space direction of a local axis from the model.
   * This accounts for the model's rotation and normalization.
   */
  const getWorldAxisDirection = useCallback(
    (axis: Axis): Vector3 => {
      if (!modelRef?.current) {
        // Fallback to world axes if model is not available
        if (axis === "x") {
          return new Vector3(1, 0, 0);
        }
        if (axis === "y") {
          return new Vector3(0, 1, 0);
        }
        return new Vector3(0, 0, 1);
      }

      // Use the shared function from meshAutoAlign
      return getWorldAxisDirectionFromModel(modelRef.current, axis);
    },
    [modelRef],
  );

  /**
   * Global pointer move handler for axis drag (attached to window during drag).
   */
  const handleGlobalPointerMove = useCallback(
    (event: PointerEvent) => {
      const currentAxis = axisDragRef.current;
      if (
        !currentAxis ||
        !dragStartPointRef.current ||
        !dragStartDomainRef.current ||
        !onPositionChange
      ) {
        return;
      }

      // Create a plane that faces the camera for better interaction
      const cameraDirection = camera.position
        .clone()
        .sub(dragStartPointRef.current)
        .normalize();

      const plane = new Plane();
      plane.setFromNormalAndCoplanarPoint(
        cameraDirection,
        dragStartPointRef.current,
      );

      const intersectionPoint = raycastOntoPlane(
        event.clientX,
        event.clientY,
        plane,
      );

      if (!intersectionPoint) {
        return;
      }

      // Get the world space direction of the axis from the model
      const axisDir = getWorldAxisDirection(currentAxis);

      const diff = intersectionPoint.clone().sub(dragStartPointRef.current);
      const delta = diff.dot(axisDir);

      const start = dragStartDomainRef.current;

      // Convert world space delta to transform.position delta
      // The base coordinate transformation (without baseRotationX) is:
      // World X = -transform.position[0]
      // World Y = transform.position[1] (but stored at index 2 in transform.position)
      // World Z = -transform.position[2] (but stored at index 1 in transform.position)
      // So: transform.position[0] = -World X, transform.position[1] = World Z, transform.position[2] = -World Y
      //
      // However, baseRotationX rotates the model around the X-axis, which changes how Y and Z map to transform.position.
      // We need to account for this rotation when converting world space movement to transform.position.
      const worldDelta = axisDir.clone().multiplyScalar(delta);
      const transformDelta = worldDeltaToTransformDelta(worldDelta);

      // Apply the delta to the start position
      const newPos: Position = [
        start[0] + transformDelta[0],
        start[1] + transformDelta[1],
        start[2] + transformDelta[2],
      ];
      onPositionChange(newPos);
    },
    [
      axisDragRef,
      dragStartPointRef,
      dragStartDomainRef,
      onPositionChange,
      raycastOntoPlane,
      camera,
      getWorldAxisDirection,
      worldDeltaToTransformDelta,
    ],
  );

  /**
   * Global pointer up handler for axis drag.
   */
  const handleGlobalPointerUp = useCallback(
    (_event: PointerEvent) => {
      if (!axisDragRef.current) {
        return;
      }

      // Remove event listeners FIRST
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }

      onEndAxisDrag();
    },
    [axisDragRef, onEndAxisDrag],
  );

  // Function to begin axis drag - sets up listeners immediately
  const beginAxisDrag = useCallback(
    (axis: Axis, startPoint: Vector3) => {
      const pos = transform?.position ?? [0, 0, 0];

      axisDragRef.current = axis;
      dragStartDomainRef.current = [pos[0], pos[1], pos[2]];
      dragStartPointRef.current = startPoint.clone();

      // Attach listeners immediately (don't wait for useEffect) - same pattern as DraggablePart
      // Create wrapper functions to ensure they work correctly
      const moveWrapper = (e: PointerEvent) => {
        handleGlobalPointerMove(e);
      };
      const upWrapper = (e: PointerEvent) => {
        handleGlobalPointerUp(e);
      };

      window.addEventListener("pointermove", moveWrapper, { capture: true });
      window.addEventListener("pointerup", upWrapper, { capture: true });
      gl.domElement.addEventListener("pointermove", moveWrapper, {
        capture: true,
      });
      gl.domElement.addEventListener("pointerup", upWrapper, { capture: true });

      // Store cleanup for listeners - need to store the wrapper functions
      const cleanup = () => {
        window.removeEventListener("pointermove", moveWrapper, {
          capture: true,
        });
        window.removeEventListener("pointerup", upWrapper, { capture: true });
        gl.domElement.removeEventListener("pointermove", moveWrapper, {
          capture: true,
        });
        gl.domElement.removeEventListener("pointerup", upWrapper, {
          capture: true,
        });
      };

      listenerCleanupRef.current = cleanup;

      onBeginAxisDrag(axis);
    },
    [
      transform,
      axisDragRef,
      dragStartPointRef,
      dragStartDomainRef,
      handleGlobalPointerMove,
      handleGlobalPointerUp,
      gl.domElement,
      onBeginAxisDrag,
    ],
  );

  // Expose beginAxisDrag via ref immediately
  beginAxisDragRef.current = beginAxisDrag;

  // Also set it in useEffect to ensure it's always up to date
  useEffect(() => {
    beginAxisDragRef.current = beginAxisDrag;
  }, [beginAxisDrag, beginAxisDragRef]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (listenerCleanupRef.current) {
        listenerCleanupRef.current();
        listenerCleanupRef.current = null;
      }
    };
  }, []);

  return null;
};

export const MeshEditorView = ({
  url,
  transform,
  contextScene,
  materialOverride,
  preferLoadedMeshMaterials = false,
  fallbackMaterial,
  enableInteractiveControls = false,
  baseRotationX = 0,
  onPositionChange,
  onModelLoaded,
  onBoundsMeasured,
  modelRef,
  referenceGuideType,
}: MeshEditorViewProps): React.JSX.Element => {
  const groupRef = useRef<Group>(null);
  const orbitControlsRef = useRef<OrbitControlsImpl>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isAxisDragging, setIsAxisDragging] = useState<AxisType>(false);
  const axisDragRef = useRef<AxisType>(false);
  const dragStartDomainRef = useRef<Position | null>(null);
  const dragStartPointRef = useRef<Vector3 | null>(null);
  const beginAxisDragRef = useRef<BeginAxisDragFn | null>(null);

  const endAxisDrag = useCallback(() => {
    if (!axisDragRef.current) return;
    axisDragRef.current = false;
    setIsAxisDragging(false);
    dragStartPointRef.current = null;
    dragStartDomainRef.current = null;
    if (orbitControlsRef.current) orbitControlsRef.current.enabled = true;
  }, []);

  const handleBeginAxisDrag = useCallback((axis: Axis) => {
    setIsAxisDragging(axis);
    if (orbitControlsRef.current) orbitControlsRef.current.enabled = false;
  }, []);

  // Handle drag state changes
  const handleDragStart = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = false;
    }
    setIsDragging(true);
  }, []);

  const handleDragEnd = useCallback(() => {
    if (orbitControlsRef.current) {
      orbitControlsRef.current.enabled = true;
    }
    setIsDragging(false);
  }, []);

  const AXIS_SNAP_THRESHOLD = 0.85;

  const shouldApplyFinalRotation =
    referenceGuideType !== ReferenceGuideType.HorizontalPlane &&
    referenceGuideType !== ReferenceGuideType.VerticalPlane;

  const handleModelCtrlClick = useCallback(
    (worldPoint: Vector3, worldNormal: Vector3 | null) => {
      if (!worldNormal || !onPositionChange) return;

      const absX = Math.abs(worldNormal.x);
      const absY = Math.abs(worldNormal.y);
      const absZ = Math.abs(worldNormal.z);
      const maxComponent = Math.max(absX, absY, absZ);

      if (maxComponent < AXIS_SNAP_THRESHOLD) return;

      let worldDelta: Vector3;
      if (absX === maxComponent) {
        worldDelta = new Vector3(-worldPoint.x, 0, 0);
      } else if (absY === maxComponent) {
        worldDelta = new Vector3(0, -worldPoint.y, 0);
      } else {
        worldDelta = new Vector3(0, 0, -worldPoint.z);
      }

      const correctedY = shouldApplyFinalRotation
        ? -worldDelta.y
        : worldDelta.y;
      const correctedZ = shouldApplyFinalRotation
        ? -worldDelta.z
        : worldDelta.z;

      const cosBaseRot = Math.cos(-baseRotationX);
      const sinBaseRot = Math.sin(-baseRotationX);
      const localY = correctedY * cosBaseRot - correctedZ * sinBaseRot;
      const localZ = correctedY * sinBaseRot + correctedZ * cosBaseRot;
      const transformDelta: Position = [-worldDelta.x, localZ, -localY];

      const pos = transform?.position ?? [0, 0, 0];
      const newPos: Position = [
        pos[0] + transformDelta[0],
        pos[1] + transformDelta[1],
        pos[2] + transformDelta[2],
      ];

      onPositionChange(newPos);
    },
    [
      onPositionChange,
      transform?.position,
      baseRotationX,
      shouldApplyFinalRotation,
    ],
  );

  // Basic loading check (could be refined based on actual loading state management)
  if (!url || !transform) {
    return (
      <div className="flex h-full items-center justify-center">
        Mesh wird geladen...
      </div>
    );
  }

  return (
    <div className="flex-1 relative h-full w-full">
      <Canvas
        camera={{ position: [0, 0, 5], fov: 50 }}
        style={{
          width: "100%",
          height: "100%",
          display: "block",
        }}
        onPointerMissed={() => {
          // Don't end axis dragging when pointer misses - allow dragging outside canvas
        }}
      >
        <OrbitControls
          ref={orbitControlsRef}
          enabled={!isDragging && !isAxisDragging}
          enableDamping={false}
          dampingFactor={0}
          rotateSpeed={1}
          panSpeed={1}
          zoomSpeed={1}
        />
        {/* Same lighting + environment as the product viewer (single source of
            truth) so meshes are lit identically. */}
        <ProductViewerStage />

        <group>
          {enableInteractiveControls ? (
            <PivotControls
              scale={100}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
            >
              <group ref={groupRef}>
                <Model
                  url={url}
                  transform={transform}
                  materialOverride={materialOverride}
                  preferLoadedMeshMaterials={preferLoadedMeshMaterials}
                  fallbackMaterial={fallbackMaterial}
                  baseRotationX={baseRotationX}
                  referenceGuideType={referenceGuideType}
                  onBoundsMeasured={onBoundsMeasured}
                  onCtrlClick={handleModelCtrlClick}
                />
                {contextScene}
              </group>
            </PivotControls>
          ) : (
            <group ref={groupRef}>
              <group>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{
                    position: [50, 0, 0] as [number, number, number],
                    rotation: [0, 0, Math.PI / 2] as [number, number, number],
                  }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("x", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#ff0000"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh
                  {...{
                    position: [50, 0, 0] as [number, number, number],
                    rotation: [0, 0, Math.PI / 2] as [number, number, number],
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#ff0000" />
                </mesh>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{ position: [0, 50, 0] as [number, number, number] }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("y", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#00ff00"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh {...{ position: [0, 50, 0] as [number, number, number] }}>
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#00ff00" />
                </mesh>
                {/* Invisible larger hitbox for easier clicking */}
                <mesh
                  {...{
                    position: [0, 0, 50] as [number, number, number],
                    rotation: [Math.PI / 2, 0, 0] as [number, number, number],
                  }}
                  onPointerDown={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (beginAxisDragRef.current) {
                      beginAxisDragRef.current("z", e.point);
                    }
                  }}
                  onPointerEnter={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0.3;
                      e.object.material.transparent = true;
                    }
                  }}
                  onPointerLeave={(e: ThreeEvent<PointerEvent>) => {
                    e.stopPropagation();
                    if (
                      e.object instanceof Mesh &&
                      e.object.material instanceof MeshBasicMaterial
                    ) {
                      e.object.material.opacity = 0;
                    }
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [8, 8, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial
                    color="#0000ff"
                    {...{ transparent: true, opacity: 0 }}
                  />
                </mesh>
                {/* Visible smaller cylinder */}
                <mesh
                  {...{
                    position: [0, 0, 50] as [number, number, number],
                    rotation: [Math.PI / 2, 0, 0] as [number, number, number],
                  }}
                >
                  <cylinderGeometry
                    {...{
                      args: [2, 2, 100, 8] as [number, number, number, number],
                    }}
                  />
                  <meshBasicMaterial color="#0000ff" />
                </mesh>
              </group>
              <Model
                url={url}
                transform={transform}
                materialOverride={materialOverride}
                preferLoadedMeshMaterials={preferLoadedMeshMaterials}
                fallbackMaterial={fallbackMaterial}
                baseRotationX={baseRotationX}
                referenceGuideType={referenceGuideType}
                onBoundsMeasured={onBoundsMeasured}
                onModelLoaded={onModelLoaded}
                onCtrlClick={handleModelCtrlClick}
              />
              {contextScene}
            </group>
          )}
        </group>

        <CameraFitter groupRef={groupRef} controlsRef={orbitControlsRef} />
        {!enableInteractiveControls && (
          <AxisDragProvider
            axisDragRef={axisDragRef}
            dragStartPointRef={dragStartPointRef}
            dragStartDomainRef={dragStartDomainRef}
            onPositionChange={onPositionChange}
            transform={transform}
            onBeginAxisDrag={handleBeginAxisDrag}
            onEndAxisDrag={endAxisDrag}
            beginAxisDragRef={beginAxisDragRef}
            modelRef={modelRef}
            baseRotationX={baseRotationX}
            referenceGuideType={referenceGuideType}
          />
        )}
      </Canvas>
    </div>
  );
};

export type { OrientationData, RotationArray };
// Export helper constants/types if needed by the container or other components
export { ORIENTATIONS, ROTATIONS };
